import { KalturaMultiResponse } from '@kaltura-ott/ott-client-types/api';
import {
  AssetUpdateAction,
  CategoryItemAddAction,
  KalturaAsset,
  KalturaCategoryItem,
  KalturaImage,
  KalturaMediaAsset,
  KalturaMediaAssetArgs,
} from '@kaltura-ott/ott-client-types/api/types';
import { AssetUpdateActionArgs } from '@kaltura-ott/ott-client-types/api/types/AssetUpdateAction';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { KuxKalturaCategoryItemArgs } from '../../interfaces';
import { kalturaClient } from '../kaltura-clent';

/**
 * Create Category and updates metadata on VirtualAsset in a single multiRequest call
 * @param category
 * @param assetMetadata
 */
export const createCategoryV2 = (
  category: KuxKalturaCategoryItemArgs,
  assetMetadata: KalturaMediaAssetArgs,
): Observable<KalturaMultiResponse> => {
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });
  const categoryAction = new CategoryItemAddAction({
    objectToAdd: new KalturaCategoryItem(category),
  });
  assetMetadata.status = true;
  delete assetMetadata?.metas?.CategoryId;
  delete assetMetadata.type;
  const assetData: AssetUpdateActionArgs = {
    asset: new KalturaMediaAsset(assetMetadata),
  } as unknown as AssetUpdateActionArgs;

  const assetAction = new AssetUpdateAction(assetData).setDependency(['id', 0, 'virtualAssetId']);

  const ml = [categoryAction, assetAction];
  return client.multiRequest(ml).pipe(
    catchError((err) => {
      return of(err);
    }),
  );
};

export interface CreateMLCategory {
  category: KuxKalturaCategoryItemArgs;
  assetMetadata: KalturaMediaAssetArgs;
  images: Array<KalturaImage>;
}

/**
 * Creates multiple Categories and updates VirtualAssets with metadata
 * @param categoriesData
 */
export const createMultiCategoryV2 = (
  categoriesData: CreateMLCategory[],
): Observable<{ asset: KalturaAsset; category: KalturaCategoryItem }[]> => {
  const client = kalturaClient();
  client.appendDefaultRequestOptions({ language: '*' });

  const categoryMLActions = categoriesData.map(({ category }) => {
    return new CategoryItemAddAction({
      objectToAdd: new KalturaCategoryItem(category),
    });
  });

  // Create all Categories
  return client.multiRequest(categoryMLActions).pipe(
    map((resp) => {
      if (resp.hasErrors()) {
        resp.forEach((r, index) => {
          if (r.error) {
            console.warn(`Request ID ${index} have ERROR --> \n`, r.error);
          }
        });
      }
      return resp;
    }),
    map((multiResponse) => {
      return multiResponse.map((r) => r.result);
    }),
    // Update VirtualAssets with all assets metadata
    mergeMap((categories: KalturaCategoryItem[]) => {
      const assetsMLActions = categoriesData.map(({ assetMetadata }, index) => {
        assetMetadata.status = true;
        delete assetMetadata?.metas?.CategoryId;
        delete assetMetadata.type;
        const assetData: AssetUpdateActionArgs = {
          asset: new KalturaMediaAsset(assetMetadata),
          id: categories[index].virtualAssetId,
        };
        return new AssetUpdateAction(assetData);
      });
      return client.multiRequest(assetsMLActions).pipe(
        map((assets: KalturaMultiResponse) => {
          return assets.map((r, index) => {
            return {
              asset: r.result,
              category: categories[index],
            };
          });
        }),
      );
    }),
  );
};
